import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';

// Per-app progress for the first-build onboarding checklist. Persisted in localStorage
// so it survives navigating to the Workflow Editor (and back to the build page).
type FirstBuildOnboardingProgress = {
  /** The user clicked through to the Workflow Editor from the checklist. */
  openedWorkflowEditor?: boolean;
  /** A successful build finished after the checklist appeared (kept once seen). */
  hadGreenBuild?: boolean;
  /** The user added/edited a trigger in the Workflow Editor (kept once true). */
  triggersEdited?: boolean;
};

const STORAGE_KEY = 'firstBuildOnboardingProgress';

type ProgressRecord = Record<string, FirstBuildOnboardingProgress>;

type UseFirstBuildOnboardingProgress = {
  progress: FirstBuildOnboardingProgress;
  patch: (update: Partial<FirstBuildOnboardingProgress>) => void;
};

/**
 * Reads and updates the onboarding progress for a single app, backed by localStorage.
 * `patch` merges into the app's entry — used by the checklist (opened Workflow Editor,
 * saw a green build) and by the Workflow Editor page (trigger edited).
 */
function useFirstBuildOnboardingProgress(appSlug: string): UseFirstBuildOnboardingProgress {
  const [record, setRecord] = useLocalStorage<ProgressRecord>(STORAGE_KEY, {});

  const patch = useCallback(
    (update: Partial<FirstBuildOnboardingProgress>) => {
      setRecord((prev) => ({ ...prev, [appSlug]: { ...prev[appSlug], ...update } }));
    },
    [appSlug, setRecord],
  );

  return { progress: record[appSlug] ?? {}, patch };
}

export default useFirstBuildOnboardingProgress;
